import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { FaTimes, FaExternalLinkAlt, FaChevronDown, FaChevronUp, FaFileExcel } from 'react-icons/fa'
import { exportSinglePartXlsx } from './atlasExportXlsx'

const API_BASE = process.env.REACT_APP_BE_MAIN_URL

const CONF_STYLES = {
  high: 'bg-green-100 text-green-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-red-100 text-red-800',
}

const MATERIAL_COLORS = {
  'Aluminum': '#60a5fa', 'Al': '#60a5fa',
  'Steel': '#f87171',
  'Titanium': '#a78bfa',
  'Stainless Steel': '#34d399',
  'Copper': '#fb923c',
}

function Thumbnail({ fileId, className = '' }) {
  const [src, setSrc] = useState(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!fileId) return
    let cancelled = false
    const token = sessionStorage.getItem('userToken')

    // Try thumbnail endpoint first (returns JSON with base64)
    axios.get(`${API_BASE}/ocr/file-thumbnail/${fileId}/`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      if (cancelled) return
      const b64 = res.data?.thumbnail_base64
      if (b64) {
        setSrc(b64)
      } else {
        // No stored thumbnail — fall back to full-image endpoint (renders from PDF)
        return axios.get(`${API_BASE}/ocr/file-full-image/${fileId}/?page=1&max_side=400`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(imgRes => {
          if (cancelled) return
          if (imgRes.data?.image_base64) {
            setSrc(imgRes.data.image_base64)
          } else {
            setError(true)
          }
        })
      }
    }).catch(() => {
      if (!cancelled) setError(true)
    })

    return () => { cancelled = true }
  }, [fileId])

  if (error || !src) {
    return (
      <div className={`bg-gray-100 flex items-center justify-center text-gray-400 text-[10px] ${className}`}>
        {error ? 'No preview' : '...'}
      </div>
    )
  }
  return <img src={src} alt="Drawing" className={`object-contain bg-white ${className}`} />
}

function DetailSection({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase tracking-wider hover:bg-gray-50"
      >
        {title}
        {open ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  )
}

function Field({ label, value, mono = false, badge = false, badgeColor }) {
  if (!value && value !== 0) return null
  return (
    <div className="flex items-start gap-2 py-1">
      <span className="text-[10px] text-gray-400 uppercase tracking-wider w-28 flex-shrink-0 pt-0.5">{label}</span>
      {badge ? (
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${badgeColor || 'bg-gray-100 text-gray-700'}`}>
          {value}
        </span>
      ) : (
        <span className={`text-xs text-gray-800 ${mono ? 'font-mono' : ''}`}>{value}</span>
      )}
    </div>
  )
}

function TravelerField({ label, value, mono = false, badge = false, badgeColor, span2 = false }) {
  if (!value && value !== 0) return null
  return (
    <div className={`flex items-start gap-2 py-0.5 ${span2 ? 'col-span-2' : ''}`}>
      <span className="text-[10px] text-gray-500 font-medium w-24 flex-shrink-0">{label}</span>
      {badge ? (
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${badgeColor || 'bg-gray-100 text-gray-700'}`}>
          {value}
        </span>
      ) : (
        <span className={`text-[11px] text-gray-900 ${mono ? 'font-mono' : ''}`}>{value}</span>
      )}
    </div>
  )
}

function DimRow({ dim, idx, critical = false }) {
  const upper = dim.upper_tol || '—'
  const lower = dim.lower_tol || '—'
  const tolStr = upper === lower ? `±${upper.replace('+', '')}` : `${upper} / ${lower}`
  return (
    <tr className={`border-b border-gray-100 ${critical ? 'bg-red-50/30 hover:bg-red-50/60' : 'hover:bg-gray-50'}`}>
      <td className="px-3 py-1 text-gray-400">{idx}</td>
      <td className="px-3 py-1 font-mono font-semibold text-gray-900">{dim.nominal}</td>
      <td className="px-3 py-1 font-mono text-gray-700">{tolStr}</td>
      {!critical && <td className="px-3 py-1 text-gray-500">{dim.unit || '—'}</td>}
      <td className="px-3 py-1 text-gray-500">{dim.type || '—'}</td>
      <td className="px-3 py-1 text-gray-500 truncate max-w-[150px]" title={dim.text_evidence}>{dim.text_evidence || '—'}</td>
    </tr>
  )
}

const FIELD_LABELS = {
  size_band: 'size band',
  dimensions: 'overall dimensions',
  material_family: 'material family',
  primary_process: 'manufacturing process',
  envelope_class: 'size envelope',
  shape_class: 'shape classification',
  machining_route: 'machining route',
}

function buildSimilaritySentence(reasons, scorePct) {
  if (!reasons || reasons.length === 0) return ''
  const parts = reasons.map(r => `${FIELD_LABELS[r.field] || r.field} (${r.value})`)
  const joined = parts.length === 1
    ? parts[0]
    : parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1]
  return `${scorePct}% similar — shares the same ${joined}.`
}

function SimilarCard({ part, onClick }) {
  const sentence = buildSimilaritySentence(part.reasons, part.score_pct)
  const pct = part.score_pct || 0

  return (
    <div
      onClick={() => onClick(part.file_id)}
      className="flex gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer border border-gray-100 transition-all hover:border-gray-200 hover:shadow-sm"
    >
      <Thumbnail fileId={part.file_id} className="w-32 h-32 rounded border border-gray-200 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold text-gray-800 truncate">{part.part_number || part.drawing_number || part.filename}</span>
          <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${
            pct >= 70 ? 'bg-green-100 text-green-800' : pct >= 40 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
          }`}>{pct}%</span>
        </div>
        <div className="text-[10px] text-gray-500 truncate">{part.part_name || part.filename}</div>
        {sentence && (
          <p className="text-[10px] text-gray-600 mt-1.5 leading-relaxed">{sentence}</p>
        )}
        <div className="flex items-center gap-2 mt-1.5">
          {part.material_family && (
            <span className="flex items-center gap-1 text-[9px] text-gray-500">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: MATERIAL_COLORS[part.material_family] || '#94a3b8' }} />
              {part.material_family}
            </span>
          )}
          {part.primary_process && (
            <span className="text-[9px] text-gray-400">{part.primary_process}</span>
          )}
          {part.machining_route && (
            <span className="text-[9px] text-gray-400">• {part.machining_route}</span>
          )}
        </div>
      </div>
    </div>
  )
}

export default function AtlasPartDetailPanel({ fileId, onClose, onNavigate }) {
  const [part, setPart] = useState(null)
  const [similar, setSimilar] = useState([])
  const [loading, setLoading] = useState(false)
  const [popupBlocked, setPopupBlocked] = useState(false)

  useEffect(() => {
    if (!fileId) return
    setLoading(true)
    const token = sessionStorage.getItem('userToken')
    axios.get(`${API_BASE}/ocr/network/atlas-part-detail/${fileId}/`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      setPart(res.data.part)
      setSimilar(res.data.similar || [])
    }).catch(() => {
      setPart(null)
      setSimilar([])
    }).finally(() => setLoading(false))
  }, [fileId])

  if (!fileId) return null

  const handleSimilarClick = (newFileId) => {
    if (onNavigate) onNavigate(newFileId)
  }

  const openDrawingViewer = async (fid) => {
    if (!fid) return
    const token = sessionStorage.getItem('userToken')

    // Try high-res first, fall back to thumbnail
    let imgData = null
    try {
      const res = await axios.get(`${API_BASE}/ocr/file-full-image/${fid}/?max_side=4096`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.data?.image_base64) imgData = res.data.image_base64
    } catch (e) { /* fall back */ }

    if (!imgData) {
      try {
        const res = await axios.get(`${API_BASE}/ocr/file-thumbnail/${fid}/`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (res.data?.thumbnail_base64) imgData = res.data.thumbnail_base64
      } catch (e) { /* nothing available */ }
    }

    if (!imgData) return

    const html = `<!DOCTYPE html>
<html><head><title>Drawing Preview</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { background: #1a1a1a; overflow: hidden; height: 100vh; width: 100vw; }
  .container { width: 100%; height: 100%; overflow: auto; display: flex; align-items: center; justify-content: center; cursor: grab; }
  .container:active { cursor: grabbing; }
  img { transform-origin: center center; border-radius: 4px; box-shadow: 0 4px 24px rgba(0,0,0,.5); max-width: 100%; max-height: 100vh; object-fit: contain; }
  .controls { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); display: flex; gap: 8px; z-index: 100; }
  .controls button { width: 40px; height: 40px; border-radius: 50%; border: none; background: rgba(255,255,255,0.9); color: #333; font-size: 20px; font-weight: bold; cursor: pointer; box-shadow: 0 2px 8px rgba(0,0,0,.3); display: flex; align-items: center; justify-content: center; transition: background 0.15s; }
  .controls button:hover { background: #fff; }
  .controls .zoom-level { background: rgba(0,0,0,0.6); color: #fff; border-radius: 16px; padding: 0 12px; font-size: 13px; display: flex; align-items: center; min-width: 52px; justify-content: center; }
  .res-badge { position: fixed; top: 12px; right: 12px; background: rgba(0,0,0,0.6); color: #aaa; font: 11px sans-serif; padding: 4px 10px; border-radius: 8px; }
</style></head>
<body>
<div class="container" id="container">
  <img id="img" src="${imgData}" alt="Drawing" />
</div>
<div class="res-badge" id="resBadge"></div>
<div class="controls">
  <button id="zoomOut" title="Zoom out">\u2212</button>
  <div class="zoom-level" id="zoomLabel">100%</div>
  <button id="zoomIn" title="Zoom in">+</button>
  <button id="zoomReset" title="Reset zoom" style="font-size:14px;">\u27F2</button>
</div>
<script>
  const img = document.getElementById('img');
  const container = document.getElementById('container');
  const label = document.getElementById('zoomLabel');
  const badge = document.getElementById('resBadge');
  let scale = 1, baseScale = 1;
  const MIN = 0.1, MAX = 8, STEP = 0.25;
  img.onload = () => {
    badge.textContent = img.naturalWidth + ' x ' + img.naturalHeight + ' px';
    const vw = window.innerWidth - 40;
    const vh = window.innerHeight - 80;
    baseScale = Math.min(vw / img.naturalWidth, vh / img.naturalHeight, 1);
    scale = 1;
    applyZoom();
  };
  function applyZoom() {
    const f = baseScale * scale;
    img.style.maxWidth = 'none';
    img.style.maxHeight = 'none';
    img.style.objectFit = '';
    img.style.width = (img.naturalWidth * f) + 'px';
    img.style.height = (img.naturalHeight * f) + 'px';
    label.textContent = Math.round(scale * 100) + '%';
    if (img.naturalWidth * f > window.innerWidth || img.naturalHeight * f > window.innerHeight) {
      container.style.overflow = 'auto';
      container.style.alignItems = 'flex-start';
      container.style.justifyContent = 'flex-start';
    } else {
      container.style.overflow = 'hidden';
      container.style.alignItems = 'center';
      container.style.justifyContent = 'center';
    }
  }
  function setZoom(s) { scale = Math.min(MAX, Math.max(MIN, s)); applyZoom(); }
  document.getElementById('zoomIn').onclick = () => setZoom(scale + STEP);
  document.getElementById('zoomOut').onclick = () => setZoom(scale - STEP);
  document.getElementById('zoomReset').onclick = () => setZoom(1);
  container.addEventListener('wheel', (e) => { e.preventDefault(); setZoom(scale + (e.deltaY > 0 ? -STEP : STEP)); }, { passive: false });
  document.addEventListener('keydown', (e) => {
    if (e.key === '+' || e.key === '=') setZoom(scale + STEP);
    else if (e.key === '-') setZoom(scale - STEP);
    else if (e.key === '0') setZoom(1);
  });
<\/script>
</body></html>`
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const win = window.open(url, '_blank')
    if (!win || win.closed || typeof win.closed === 'undefined') {
      setPopupBlocked(true)
    }
    setTimeout(() => URL.revokeObjectURL(url), 10000)
  }

  // Classify dimensions: critical (tight tolerance) vs standard
  const dimensions = part?.dimensions || []
  const gdtFrames = part?.gdt_frames || []
  const notes = part?.notes || []
  const generalTol = part?.general_tolerance || {}

  // Sort dims: tightest tolerance first
  const sortedDims = [...dimensions].sort((a, b) => {
    const bandA = Math.abs(parseFloat(a.upper_tol || 999) - parseFloat(a.lower_tol || -999))
    const bandB = Math.abs(parseFloat(b.upper_tol || 999) - parseFloat(b.lower_tol || -999))
    return bandA - bandB
  })

  const criticalDims = sortedDims.filter(d => {
    if (d.tolerance_is_general) return false
    const band = Math.abs(parseFloat(d.upper_tol || 0) - parseFloat(d.lower_tol || 0))
    return band > 0 && band <= 0.1
  })
  const standardDims = sortedDims.filter(d => !criticalDims.includes(d))

  return (
    <div className="fixed inset-y-0 right-0 w-[50vw] bg-white shadow-2xl z-50 flex flex-col border-l border-gray-200 animate-slide-in">
      {/* Traveler Header — mimics shop floor document */}
      <div className="flex items-center justify-between px-4 py-2 border-b-2 border-gray-800 bg-white">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Drawing Atlas</div>
          <div className="h-4 w-px bg-gray-300" />
          <div className="text-sm font-bold font-mono text-gray-900 truncate">
            {part?.drawing_number || part?.part_number || 'Loading...'}
          </div>
          {part?.revision && (
            <span className="text-[10px] font-bold bg-gray-900 text-white px-1.5 py-0.5 rounded">REV {part.revision}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {part && (
            <button
              onClick={() => exportSinglePartXlsx(part)}
              className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100 transition-colors"
              title="Export to Excel"
            >
              <FaFileExcel size={10} /> XLSX
            </button>
          )}
          {part?.file_id && (
            <a href={`/workspace?file=${part.file_id}`} className="text-gray-400 hover:text-gray-700 transition-colors" title="Open file detail">
              <FaExternalLinkAlt size={11} />
            </a>
          )}
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 transition-colors p-1">
            <FaTimes size={14} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Loading...</div>
      ) : !part ? (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Part not found</div>
      ) : (
        <div className="flex-1 overflow-y-auto">

          {/* Drawing Preview */}
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            {popupBlocked && (
              <div className="mb-2 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <span className="text-amber-500 flex-shrink-0 text-sm">&#9888;</span>
                <span className="text-xs text-amber-800">
                  Pop-up was blocked by your browser. Please allow pop-ups for this site to view the full-screen drawing.
                </span>
                <button onClick={() => setPopupBlocked(false)} className="ml-auto text-amber-500 hover:text-amber-700 text-sm font-bold">&times;</button>
              </div>
            )}
            <div
              onClick={() => openDrawingViewer(part.file_id)}
              className="cursor-pointer hover:ring-2 hover:ring-gray-400/30 rounded transition-all"
              title="Click to open drawing viewer"
            >
              <Thumbnail fileId={part.file_id} className="w-full h-64 rounded border border-gray-300" />
            </div>
          </div>

          {/* SECTION 1: Part Identification */}
          <div className="border-b border-gray-200">
            <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
              <span className="text-[10px] font-bold text-gray-600 uppercase tracking-widest">1 — Identification</span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 px-4 py-2 text-[11px]">
              <TravelerField label="Drawing #" value={part.drawing_number} mono />
              <TravelerField label="Part #" value={part.part_number} mono />
              <TravelerField label="Part Name" value={part.part_name} span2 />
              <TravelerField label="Type" value={part.drawing_type} />
              <TravelerField label="Confidence" value={part.confidence} badge badgeColor={CONF_STYLES[part.confidence]} />
              <TravelerField label="Released" value={part.released_status} badge badgeColor={part.released_status === 'released' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'} />
              <TravelerField label="Assembly" value={part.top_level_assembly_pn} mono />
            </div>
          </div>

          {/* SECTION 2: Material & Stock */}
          <div className="border-b border-gray-200">
            <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
              <span className="text-[10px] font-bold text-gray-600 uppercase tracking-widest">2 — Material &amp; Stock</span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 px-4 py-2 text-[11px]">
              <TravelerField label="Family" value={part.material_family} badge badgeColor="bg-blue-100 text-blue-800" />
              <TravelerField label="Specification" value={part.material_normalized} />
              <TravelerField label="Raw Callout" value={part.material_callout_raw} span2 />
              <TravelerField label="Condition" value={part.condition_temper} />
              <TravelerField label="Coating" value={part.coating} />
              <TravelerField label="Stock Form" value={part.stock_form} />
              <TravelerField label="Hardness" value={part.hardness} />
            </div>
          </div>

          {/* SECTION 3: Geometry & Envelope */}
          <div className="border-b border-gray-200">
            <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
              <span className="text-[10px] font-bold text-gray-600 uppercase tracking-widest">3 — Geometry &amp; Envelope</span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 px-4 py-2 text-[11px]">
              <TravelerField label="Envelope" value={part.envelope_class} badge />
              <TravelerField label="Size Band" value={part.size_band} badge />
              <TravelerField label="Shape" value={part.shape_class} />
              <TravelerField label="Subclass" value={part.shape_subclass} />
              {(part.bbox_length_mm || part.bbox_width_mm || part.bbox_height_mm) && (
                <TravelerField label="Bounding Box" value={`${part.bbox_length_mm?.toFixed(1) || '?'} × ${part.bbox_width_mm?.toFixed(1) || '?'} × ${part.bbox_height_mm?.toFixed(1) || '?'} mm`} mono span2 />
              )}
              <TravelerField label="Mass" value={part.mass_kg != null ? (part.mass_kg < 1 ? `${(part.mass_kg * 1000).toFixed(1)} g` : `${part.mass_kg.toFixed(2)} kg`) : null} mono />
              <TravelerField label="Buy-to-Fly" value={part.buy_to_fly != null ? `${part.buy_to_fly.toFixed(1)}:1` : null} mono />
              {part.gauge_mm && <TravelerField label="Gauge" value={`${part.gauge_mm.toFixed(2)} mm`} mono />}
            </div>
          </div>

          {/* SECTION 4: Routing / Operations */}
          <div className="border-b border-gray-200">
            <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
              <span className="text-[10px] font-bold text-gray-600 uppercase tracking-widest">4 — Routing</span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 px-4 py-2 text-[11px]">
              <TravelerField label="Primary Process" value={part.primary_process} badge badgeColor="bg-emerald-100 text-emerald-800" />
              <TravelerField label="Route" value={part.machining_route} />
              <TravelerField label="Axes" value={part.axis_count} />
              <TravelerField label="Setups" value={part.setup_count} />
              <TravelerField label="Bends" value={part.bend_count} />
              <TravelerField label="Complexity" value={part.complexity_tier} badge badgeColor="bg-purple-100 text-purple-800" />
              {part.secondary_processes?.length > 0 && (
                <div className="col-span-2 flex items-start gap-2 py-0.5">
                  <span className="text-[10px] text-gray-500 font-medium w-24 flex-shrink-0">Secondary</span>
                  <div className="flex flex-wrap gap-1">
                    {part.secondary_processes.map((p, i) => (
                      <span key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded text-[10px]">{p}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* SECTION 5: General Tolerance Block */}
          {(generalTol.linear || generalTol.angular || part.units_declared) && (
            <div className="border-b border-gray-200">
              <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
                <span className="text-[10px] font-bold text-gray-600 uppercase tracking-widest">5 — General Tolerances</span>
              </div>
              <div className="grid grid-cols-3 gap-x-4 gap-y-1 px-4 py-2 text-[11px]">
                <TravelerField label="Units" value={part.units_declared} />
                <TravelerField label="Linear" value={generalTol.linear} mono />
                <TravelerField label="Angular" value={generalTol.angular} mono />
              </div>
            </div>
          )}

          {/* SECTION 6: Critical Dimensions (tight tolerance) */}
          {criticalDims.length > 0 && (
            <div className="border-b border-gray-200">
              <div className="px-4 py-2 bg-red-50 border-b border-red-200">
                <span className="text-[10px] font-bold text-red-700 uppercase tracking-widest">6 — Critical Dimensions ({criticalDims.length})</span>
                <span className="ml-2 text-[9px] text-red-500 font-normal">tolerance band ≤ 0.1mm</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[10px]">
                  <thead className="bg-red-50/50">
                    <tr className="border-b border-gray-200">
                      <th className="text-left px-3 py-1.5 font-semibold text-gray-600">#</th>
                      <th className="text-left px-3 py-1.5 font-semibold text-gray-600">Nominal</th>
                      <th className="text-left px-3 py-1.5 font-semibold text-gray-600">Tolerance</th>
                      <th className="text-left px-3 py-1.5 font-semibold text-gray-600">Type</th>
                      <th className="text-left px-3 py-1.5 font-semibold text-gray-600">Evidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {criticalDims.map((d, i) => (
                      <DimRow key={i} dim={d} idx={i + 1} critical />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SECTION 7: GD&T Frames */}
          {gdtFrames.length > 0 && (
            <div className="border-b border-gray-200">
              <div className="px-4 py-2 bg-amber-50 border-b border-amber-200">
                <span className="text-[10px] font-bold text-amber-700 uppercase tracking-widest">7 — GD&amp;T Frames ({gdtFrames.length})</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[10px]">
                  <thead className="bg-amber-50/50">
                    <tr className="border-b border-gray-200">
                      <th className="text-left px-3 py-1.5 font-semibold text-gray-600">#</th>
                      <th className="text-left px-3 py-1.5 font-semibold text-gray-600">Symbol</th>
                      <th className="text-left px-3 py-1.5 font-semibold text-gray-600">Tolerance</th>
                      <th className="text-left px-3 py-1.5 font-semibold text-gray-600">Feature</th>
                      <th className="text-left px-3 py-1.5 font-semibold text-gray-600">Datums</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gdtFrames.map((g, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-amber-50/30">
                        <td className="px-3 py-1 text-gray-400">{i + 1}</td>
                        <td className="px-3 py-1 font-mono font-semibold text-amber-800">{g.symbol}</td>
                        <td className="px-3 py-1 font-mono">{g.tolerance}</td>
                        <td className="px-3 py-1 text-gray-700">{g.affected_feature || '—'}</td>
                        <td className="px-3 py-1 font-mono text-gray-500">
                          {[g.datum_primary, g.datum_secondary, g.datum_tertiary].filter(d => d && d !== 'null').join(' | ') || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* SECTION 8: All Dimensions */}
          <div className="border-b border-gray-200">
            <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
              <span className="text-[10px] font-bold text-gray-600 uppercase tracking-widest">
                {criticalDims.length > 0 ? '8' : '6'} — All Dimensions ({dimensions.length})
              </span>
              {part.tightest_linear_mm != null && (
                <span className="ml-2 text-[9px] text-gray-500 font-normal">tightest: ±{part.tightest_linear_mm.toFixed(4)} mm</span>
              )}
            </div>
            {dimensions.length === 0 ? (
              <div className="px-4 py-3 text-[11px] text-gray-400 italic">No dimensions extracted for this drawing.</div>
            ) : (
              <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                <table className="w-full text-[10px]">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr className="border-b border-gray-200">
                      <th className="text-left px-3 py-1.5 font-semibold text-gray-600">#</th>
                      <th className="text-left px-3 py-1.5 font-semibold text-gray-600">Nominal</th>
                      <th className="text-left px-3 py-1.5 font-semibold text-gray-600">Tolerance</th>
                      <th className="text-left px-3 py-1.5 font-semibold text-gray-600">Unit</th>
                      <th className="text-left px-3 py-1.5 font-semibold text-gray-600">Type</th>
                      <th className="text-left px-3 py-1.5 font-semibold text-gray-600">Evidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDims.map((d, i) => (
                      <DimRow key={i} dim={d} idx={i + 1} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* SECTION: Notes */}
          {notes.length > 0 && (
            <div className="border-b border-gray-200">
              <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
                <span className="text-[10px] font-bold text-gray-600 uppercase tracking-widest">Notes ({notes.length})</span>
              </div>
              <div className="px-4 py-2 space-y-1.5">
                {notes.map((n, i) => (
                  <div key={i} className="flex gap-2 text-[11px]">
                    <span className="text-gray-400 font-mono flex-shrink-0 w-5 text-right">{n.number || i + 1}.</span>
                    <span className="text-gray-700">{n.text}</span>
                    {n.classification && n.classification !== 'general' && (
                      <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 flex-shrink-0">{n.classification}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SECTION: Supply Chain (if available) */}
          {(part.supplier_names?.length > 0 || part.total_spend > 0) && (
            <div className="border-b border-gray-200">
              <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
                <span className="text-[10px] font-bold text-gray-600 uppercase tracking-widest">Supply Chain</span>
              </div>
              <div className="px-4 py-2">
                {part.supplier_names?.length > 0 && (
                  <div className="flex items-start gap-2 py-0.5 text-[11px]">
                    <span className="text-gray-500 font-medium w-24 flex-shrink-0">Suppliers</span>
                    <div className="flex flex-wrap gap-1">
                      {part.supplier_names.map((s, i) => (
                        <span key={i} className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-[10px] font-medium">{s}</span>
                      ))}
                    </div>
                  </div>
                )}
                {part.total_spend > 0 && (
                  <TravelerField label="Total Spend" value={`$${part.total_spend.toLocaleString()}`} />
                )}
              </div>
            </div>
          )}

          {/* Similar Parts — at bottom */}
          {similar.length > 0 && (
            <div className="border-t-2 border-gray-800 mt-2">
              <div className="px-4 py-3">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] font-bold text-gray-600 uppercase tracking-widest">
                    Similar Parts ({similar.length})
                  </span>
                  <div className="relative group/tip">
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-[9px] font-bold text-gray-500 cursor-help">i</span>
                    <div className="absolute left-6 top-0 z-50 hidden group-hover/tip:block w-72 bg-gray-900 text-gray-100 text-[10px] leading-relaxed rounded-lg shadow-xl p-3">
                      <p className="font-semibold mb-1 text-[11px]">Similarity Algorithm</p>
                      <p className="mb-2">Parts are scored on a weighted scale (max 18 pts) then shown as a percentage:</p>
                      <ul className="space-y-0.5 ml-2">
                        <li><span className="font-mono text-green-300">4 pts</span> — Same size band</li>
                        <li><span className="font-mono text-green-300">3 pts</span> — Dimensional proximity (≤10% diff → 3, ≤25% → 2, ≤50% → 1)</li>
                        <li><span className="font-mono text-green-300">3 pts</span> — Same material family</li>
                        <li><span className="font-mono text-green-300">3 pts</span> — Same manufacturing process</li>
                        <li><span className="font-mono text-green-300">2 pts</span> — Same size envelope class</li>
                        <li><span className="font-mono text-green-300">2 pts</span> — Same shape classification</li>
                        <li><span className="font-mono text-green-300">1 pt</span>&nbsp; — Same machining route</li>
                      </ul>
                      <p className="mt-2 text-gray-400">Size accounts for up to 50% of the total score. Only parts in the same workspace folder are compared.</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  {similar.map(s => (
                    <SimilarCard key={s.file_id} part={s} onClick={handleSimilarClick} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
